import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Loader2, Check, ListChecks, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const categories = ["Aptitude", "Reasoning", "Technical", "Verbal", "Versant", "AML & CTF", "Financial Crime", "Interview Prep", "Medical Billing", "AR Calling", "SAP MM"];
const difficulties = ["easy", "medium", "hard"];

export default function AIGenerator() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("quiz");
  const [form, setForm] = useState({ topic: "", category: "Aptitude", difficulty: "medium", num_questions: 5 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Enter a topic."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const endpoint = tab === "quiz" ? "/admin/generate-quiz" : "/admin/generate-course";
      const { data } = await api.post(endpoint, form);
      setResult(tab === "quiz" ? { type: "quiz", ...data.quiz } : { type: "course", ...data.course });
      qc.invalidateQueries({ queryKey: [tab === "quiz" ? "quizzes" : "courses"] });
      toast.success(tab === "quiz" ? "Quiz generated!" : "Course generated!");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      toast.error("Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="ai-generator-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin Tools</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-primary" /> AI Generator
        </h1>
        <p className="text-muted-foreground mt-1">Generate custom quizzes and course lessons instantly with Gemini 3.5 Flash.</p>
      </div>

      <div className="p-6 rounded-md border border-border bg-card">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setResult(null); }}>
          <TabsList className="grid grid-cols-2 mb-6">
            <TabsTrigger value="quiz" data-testid="tab-quiz"><ListChecks className="w-4 h-4 mr-2" /> Quiz</TabsTrigger>
            <TabsTrigger value="course" data-testid="tab-course"><BookOpen className="w-4 h-4 mr-2" /> Course</TabsTrigger>
          </TabsList>

          <div className="space-y-4">
            <div>
              <Label htmlFor="topic">Topic</Label>
              <Input id="topic" data-testid="gen-topic-input" value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="mt-1.5 rounded-md" placeholder="e.g. Time and Work, Data Structures, Synonyms..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5 rounded-md" data-testid="gen-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger className="mt-1.5 rounded-md" data-testid="gen-difficulty-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {difficulties.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {tab === "quiz" && (
              <div>
                <Label htmlFor="numq">Number of questions</Label>
                <Input id="numq" type="number" min={3} max={15} data-testid="gen-numq-input" value={form.num_questions}
                  onChange={(e) => setForm({ ...form, num_questions: parseInt(e.target.value) || 5 })}
                  className="mt-1.5 rounded-md w-32" />
              </div>
            )}

            {error && <p className="text-sm text-destructive" data-testid="gen-error">{error}</p>}

            <Button onClick={generate} disabled={loading} className="rounded-md gap-2 h-11" data-testid="generate-button">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate {tab === "quiz" ? "Quiz" : "Course"}</>}
            </Button>
          </div>
        </Tabs>

        {loading && (
          <div className="mt-6 space-y-2">
            <div className="h-1 rounded-full tracing-beam" />
            <p className="text-xs text-muted-foreground font-mono text-center">AI is crafting your content...</p>
          </div>
        )}
      </div>

      {result && (
        <div className="p-6 rounded-md border border-success/40 bg-card fade-in-up" data-testid="gen-result">
          <div className="flex items-center gap-2 mb-4 text-success">
            <Check className="w-5 h-5" />
            <span className="font-heading font-bold">Successfully generated!</span>
          </div>
          <h3 className="font-heading font-bold text-xl mb-2">{result.title}</h3>
          {result.type === "quiz" ? (
            <>
              <p className="text-sm text-muted-foreground mb-4">{result.questions.length} questions · {result.category} · {result.difficulty}</p>
              <ol className="space-y-2 list-decimal ml-5 text-sm">
                {result.questions.slice(0, 3).map((q, i) => <li key={i} className="text-muted-foreground">{q.question}</li>)}
              </ol>
              {result.questions.length > 3 && <p className="text-xs text-muted-foreground mt-2">+ {result.questions.length - 3} more...</p>}
              <Link to={`/quizzes/${result.id}`} className="inline-block mt-4">
                <Button variant="outline" className="rounded-md" data-testid="view-generated-quiz">Take this Quiz</Button>
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">{result.description}</p>
              <Link to={`/courses/${result.id}`}>
                <Button variant="outline" className="rounded-md" data-testid="view-generated-course">View Course</Button>
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
